import axios from 'axios';
import dayjs from 'dayjs';
import {
  store,
  SET_USER_MUTATION,
  REPORTS_SCOPE_TYPES,
  SET_USER_PROFILE_MUTATION,
} from '@/store';
import UserSettings from '@/providers/UserSettings';
import isNil from 'lodash/isNil';
import qs from 'qs';

const getApiUrl = function () {
   let url = `${process.env.VUE_APP_API_HOST}/index.php`;
  if (location.protocol === 'https:' && url.indexOf(location.protocol) === -1) {
    return replaceProtocol(url, true);
  } else if (
    location.protocol === 'http:' &&
    url.indexOf(location.protocol) === -1
  ) {
    return replaceProtocol(url);
  }
  return url;
};

export const didAbort = (error) => axios.isCancel(error);
export const getCancelSource = () => axios.CancelToken.source();

const createPostFnWithAbort =
  (component = {}, requestCancelOptionName = '') =>
  (url, data, config = {}) => {
    if (!component.$options) {
      return requestHandler.post(url, data, config);
    }
    const { cancel, token } = getCancelSource();
    if (typeof component.$options[requestCancelOptionName] === 'function') {
      component.$options[requestCancelOptionName]();
    }
    component.$options[requestCancelOptionName] = cancel;
    return requestHandler.post(url, data, {
      ...config,
      cancelToken: token,
    });
  };

const createPutFnWithAbort =
  (component = {}, requestCancelOptionName = '') =>
  (url, data, config = {}) => {
    if (!component.$options) {
      return requestHandler.put(url, data, config);
    }
    const { cancel, token } = getCancelSource();
    if (typeof component.$options[requestCancelOptionName] === 'function') {
      component.$options[requestCancelOptionName]();
    }
    component.$options[requestCancelOptionName] = cancel;
    return requestHandler.put(url, data, {
      ...config,
      cancelToken: token,
    });
  };

const USER_QUESTIONNAIRE_EXTRA_FIELDS = [
  'questionnaire',
  'questionnaire.sections.questionSections.question.variables.variableOptions',
  'questionnaire.sections.questionSections.question.relatedJobQuestion.jobQuestionVariables.defaultJobProfileQuestionResponses',
  'questionnaire.sections.questionSections.question.relatedJobQuestion.jobQuestionVariables.variableOptions',
  'questionnaire.membersCompletionPercentage',
  'questionnaire.submittedRandomSurveysCount',
  'questionnaire.randomSurveysCount',
  'questionnaire.submittedConvenienceSurveysCount',
  'questionnaire.convenienceSurveysCount',
  'questionnaire.societyMembershipTypes',
  'questionnaire.survey.questionsCompleted',
];

const USER_EXTRA_FIELDS = [
  'primaryQuestionnaireComparisons',
  'boardCertifications',
  'defaultJobProfile',
  'hasStudyWithJobPreferences',
  'completedStudyWithJobPreferences',
  'email_confirm_token',
  'uncompleted_surveys',
  'completed_surveys',
  'specialty',
  'subspecialty',
  'society',
  'roles',
  'profile',
];

const SOCIETY_MEMBER_EXTRA_FIELDS = [
  'number_of_links_sent',
  'send_reg_email_date',
  'society_member_uid',
  'status',
  'send_reg_email',
  'zip_code',
  'specialty',
  'subspecialty',
  'gender',
  'ethnicity',
  'member_type',
  'year_of_birth',
];

const replaceProtocol = function (url, toHttps = false) {
  let newUrl;
  if (toHttps) {
    newUrl = url.replace('http:', 'https:');
  } else {
    newUrl = url.replace('https:', 'http:');
  }
  return newUrl;
};

export const apiUrl = getApiUrl();
export const requestHandler = axios.create({
  baseURL: apiUrl,
  paramsSerializer: (params) => {
    return qs.stringify(params);
  },
});

/**
 * Interface to the Yii api
 */
export default {
  getCurrentUser: function (vue) {
    return this.getResources(
      vue,
      'user/get-current-sign-user',
      true,
      USER_EXTRA_FIELDS
    );
  },

  getCurrentUserQuestionnaireData: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'user/get-current-sign-user',
        expand: USER_QUESTIONNAIRE_EXTRA_FIELDS.join(','),
      },
    });
    return data && Array.isArray(data.items) && data.items[0]
      ? data.items[0]
      : {};
  },

  signInUser: async function (email, password) {
    let response = await requestHandler.post(
      '/',
      {
        email,
        password,
      },
      {
        params: {
          r: 'auth/signin',
          expand: USER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return response.data;
  },

  signUpUser: async function (signUpData) {
    let response = await requestHandler.post('/', signUpData, {
      params: {
        r: 'auth/signup',
        expand: USER_EXTRA_FIELDS.join(','),
      },
    });
    return response.data;
  },

  nonSocietyUserSignUp: async function (signUpData) {
    let response = await requestHandler.post('/', signUpData, {
      params: {
        r: 'auth/non-society-signup',
        expand: USER_EXTRA_FIELDS.join(','),
      },
    });
    return response.data;
  },

  signUpRecruiter: async function (signUpData) {
    let { data } = await requestHandler.post('/', signUpData, {
      params: {
        r: 'auth/recruiter-signup',
        expand: USER_EXTRA_FIELDS.join(','),
      },
    });
    return data;
  },

  validateToken: async function (token) {
    const { data } = await requestHandler.post(
      '/',
      { token },
      {
        params: {
          r: 'auth/validate-token',
        },
      }
    );

    return data;
  },

  validateEmail: async function (societyId, email) {
    const { data } = await requestHandler.post(
      '/',
      { societyId, email },
      {
        params: {
          r: 'auth/validate-email',
        },
      }
    );

    return data;
  },

  validateEmailAndToken: async function (email, token) {
    const { data } = await requestHandler.post(
      '/',
      { email, token },
      {
        params: {
          r: 'auth/validate-email-and-token',
        },
      }
    );

    return data;
  },

  sendRegistrationToken: async function (email) {
    const { data } = await requestHandler.post(
      '/',
      { email },
      {
        params: {
          r: 'auth/need-token',
        },
      }
    );
    return data;
  },

  memberByEmailSociety: async function (email, societyId) {
    const { data } = await requestHandler.post(
      '/',
      { email, societyId },
      {
        params: {
          r: 'auth/check-is-email-valid',
          expand: [
            'society',
            'zip_code',
            'gender',
            'ethnicity',
            'year_of_birth',
            'societyMemberTypeMapping',
          ].join(','),
        },
      }
    );
    return data;
  },

  checkIsEmailSocietyValid: async function (email, societyId) {
    const { data } = await requestHandler.post(
      '/',
      { email, societyId },
      {
        params: {
          r: 'auth/check-is-email-society-valid',
        },
      }
    );
    return data;
  },

  // ------- user ---------

  getAllUsers(vue, filters, listParams) {
    return this.getResources(
      vue,
      'user/all-users',
      false,
      [],
      filters,
      listParams
    );
  },

  maybeUpdateSettings(user) {
    const settings = store.state.userSettings;
    settings.demoMode = true; // TEMP
    if (!settings.comparisons) {
      settings.comparisons = UserSettings.defaultComparisons;
      this.updateUser(null, user);
    }
  },

  getUser(vue, email) {
    const filters = {
      email: { field: 'email', value: email },
    };

    return this.getResources(vue, 'user', true, USER_EXTRA_FIELDS, filters);
  },

  async updateUserByAdmin(user) {
    if (!store.getters.isAuthenticated) return;
    const { data } = await requestHandler.put(
      `/?r=user/admin-update&id=${user.id}`,
      user,
      {
        params: {
          expand: [
            ...USER_EXTRA_FIELDS,
            'is_email_invalid',
            'is_email_optout',
          ].join(','),
        },
      }
    );
    return data;
  },

  async createUser(newUser) {
    if (!store.getters.isAuthenticated || !store.getters.userIsGMAdmin) return;
    const { data } = await requestHandler.post(
      '/?r=user/admin-create',
      newUser,
      {
        params: {
          expand: [
            ...USER_EXTRA_FIELDS,
            'is_email_invalid',
            'is_email_optout',
          ].join(','),
        },
      }
    );
    return data;
  },

  async updateUserByMSSAdmin(user) {
    if (!store.getters.isAuthenticated) return;
    user.settings = JSON.stringify(store.state.userSettings);
    const { data } = await requestHandler.put(
      `/?r=user/mss-admin-update&id=${user.id}`,
      user
    );
    return data;
  },

  updateUser: async function (component, user, refreshAfter = false) {
    if (!store.getters.isAuthenticated) return;
    user.settings = JSON.stringify(store.state.userSettings);
    // eslint-disable-next-line no-unused-vars
    const { id, email, roles, society, ...restUserData } = user;
    const putWithAbort = createPutFnWithAbort(component, 'updateUserRequest');
    const extraFields = refreshAfter
      ? [...USER_EXTRA_FIELDS, ...USER_QUESTIONNAIRE_EXTRA_FIELDS]
      : USER_EXTRA_FIELDS;
    const { data } = await putWithAbort(`/?r=user/update`, restUserData, {
      params: {
        expand: extraFields.join(','),
      },
    });
    if (refreshAfter) {
      store.commit(SET_USER_MUTATION, data); // refresh user data, e.g. surveys
    }
    return data;
  },

  updateUserProfile: async function (user, refreshAfter) {
    if (!store.getters.isAuthenticated) return;

    // eslint-disable-next-line no-unused-vars
    const { id, email, roles, society, ...restUserParams } = user;

    return await requestHandler
      .put('/?r=user/update-profile', restUserParams, {
        params: {
          expand: USER_EXTRA_FIELDS.join(','),
        },
      })
      .then((response) => {
        if (refreshAfter) {
          store.commit(SET_USER_PROFILE_MUTATION, response.data); // refresh user data, e.g. surveys
        }
      });
  },

  deleteUser(id) {
    return requestHandler.delete(`/?r=user/delete&id=${id}`);
  },

  resetUser: async function (id) {
    let response = await requestHandler.post(
      '/',
      {},
      {
        params: {
          r: 'user/reset',
          id,
        },
      }
    );
    return response.data;
  },

  saveNewPasswordByAdmin(id, password, confirmPassword) {
    return requestHandler.put(`/?r=user/save-new-password-by-admin&id=${id}`, {
      password,
      confirm_password: confirmPassword,
    });
  },

  updateTemporaryPassword: function (password) {
    return requestHandler.put('/?r=user/update-temporary-password', {
      password,
    });
  },

  UpdatePasswordByUser: function (password, confirmPassword) {
    return requestHandler.put('/?r=user/update-password-by-user', {
      password,
      confirm_password: confirmPassword,
    });
  },

  getUserByMemberId: async function (id) {
    const response = await requestHandler.get(
      `/?r=user/get-by-member-id&id=${id}`
    );
    return response.data;
  },

  findUserEmailByIds: async function (ids) {
    const { data } = await requestHandler.post(
      '/',
      { ids },
      {
        params: {
          r: 'user/user-emails-by-ids',
        },
      }
    );
    return data;
  },

  emailSettingsBulkUpdate: async function (updateData) {
    const { data } = await requestHandler.post(
      '/',
      { ...updateData },
      {
        params: {
          r: 'user/email-settings-bulk-update',
        },
      }
    );
    return data;
  },

  userEmailByIdsAsCsv: async function (ids) {
    const response = await requestHandler.post(
      '/',
      { ids },
      {
        responseType: 'arraybuffer',
        params: {
          r: 'user/user-emails-by-ids-as-csv',
        },
      }
    );
    let blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'UserEmails.csv');
    link.click();
    link.remove();
  },

  isUserProfileFilled: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'user/is-profile-filled',
      },
    });
    return data;
  },

  // ----- password reset ------

  requestPasswordReset(email) {
    // console.log(email);
    return requestHandler.post('/?r=auth/request', {
      email: email,
    });
  },

  resendPasswordReset(token) {
    return requestHandler.post('/?r=auth/resend', {
      password_reset_token: token,
    });
  },

  checkTokenIsExpired(token) {
    return requestHandler.post('/?r=auth/check-expired', {
      password_reset_token: token,
    });
  },

  saveNewPassword(token, newPassword) {
    return requestHandler.post('/?r=auth/confirm', {
      token,
      password: newPassword,
    });
  },

  // ------- societies ---------

  getSocieties(vue, filters, listParams) {
    return this.getResources(vue, 'society', false, [], filters, listParams);
  },

  getAllSocieties() {
    return this.getResources(null, 'society', false, [], [], {
      perPage: 10000,
      sortBy: 'name',
    });
  },

  getSociety(vue, id) {
    const filters = {
      id: { field: 'id', value: id },
    };
    const expands = [
      'internalPerson',
      'societyMemberTypeMappings',
      'certifyingBoards',
      'secret_key',
    ];
    return this.getResources(vue, 'society', true, expands, filters);
  },

  getSocietyByUrlCode: async function (code) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'society/get-by-url-code',
        code,
      },
    });
    return data;
  },

  createSociety: async function (society) {
    let formData = new FormData();
    for (let key in society) {
      if (society.hasOwnProperty(key) && !isNil(society[key])) {
        formData.append(key, society[key]);
      }
    }
    const expand = [
      'internalPerson',
      'societyMemberTypeMappings',
      'certifyingBoards',
      'secret_key',
    ].join(',');
    const { data } = await requestHandler.post(`/`, formData, {
      params: {
        r: 'society/create',
        expand,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  updateSociety: async function (society) {
    let formData = new FormData();
    for (let key in society) {
      if (society.hasOwnProperty(key) && !isNil(society[key])) {
        formData.append(key, society[key]);
      }
    }
    const expand = [
      'internalPerson',
      'societyMemberTypeMappings',
      'certifyingBoards',
      'secret_key',
    ].join(',');
    const { data } = await requestHandler.put(`/`, formData, {
      params: {
        r: `society/update`,
        id: society.id,
        expand,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  updateSocietyMembers: async function (data) {
    let formData = new FormData();

    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        formData.append(key, data[key]);
      }
    }

    const response = await requestHandler.put(``, formData, {
      params: {
        r: `society/update-members`,
        id: data.id,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteSociety(id) {
    return requestHandler.delete(`/?r=society/delete&id=${id}`);
  },

  generateNewSecretKey: async function (id) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'society/generate-new-secret-key',
        id,
      },
    });
    return data;
  },

  getSocietyStatisticsOfMembers: async function (id) {
    const response = await requestHandler.get(
      `/?r=society/get-statistics-of-members&id=${id}`
    );
    return response.data;
  },

  getSocietyStatusOfQuestionnaire: async function (
    questionnaireId,
    societyId = null
  ) {
    const response = await requestHandler.get(
      '/?r=society/get-status-of-questionnaire',
      {
        params: {
          questionnaireId,
          societyId,
        },
      }
    );
    return response.data;
  },

  getDistributionOptionData: async function (societyId) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'society/get-distribution-option-data',
        societyId,
      },
    });
    return response.data;
  },

  sendDistributionEmail: async function (data) {
    const response = await requestHandler.post('/', data, {
      params: {
        r: 'society/send-distribution-email',
      },
    });
    return response.data;
  },

  sendDistributionTestEmail: async function (data) {
    const response = await requestHandler.post('/', data, {
      params: {
        r: 'society/send-distribution-test-email',
      },
    });
    return response.data;
  },

  sendTestEmail: async function (data) {
    const response = await requestHandler.post('/', data, {
      params: {
        r: 'society/send-test-email',
      },
    });
    return response.data;
  },

  getSocietyQuestionnaires: async function (societyId) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'society/get-questionnaires',
        societyId,
      },
    });
    return response.data;
  },

  getNonSocietyQuestionnaires: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'questionnaire/get-for-non-society',
      },
    });
    return data && Array.isArray(data) ? data : [];
  },

  getQuestionnaireOptions: async function (societyId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'questionnaire/get-prior-questionnaire-options',
        societyId
      },
    });
    return data && Array.isArray(data) ? data : [];
  },
  // ------- specialties ---------

  getSpecialties(vue, filters, listParams) {
    return this.getResources(
      vue,
      'specialty',
      false,
      ['usersCount'],
      { ...filters, is_master: { field: 'is_master', value: 0 } },
      listParams
    );
  },

  getMasterSpecialties(vue, filters, listParams) {
    return this.getResources(
      vue,
      'specialty',
      false,
      [],
      { ...filters, is_master: { field: 'is_master', value: 1 } },
      listParams
    );
  },

  getAllSpecialties() {
    return this.getResources(null, 'specialty', false, ['societiesCount', 'usersCount','jobProfilesCount'], [], {
      perPage: 10000,
      sortBy: 'title',
    });
  },

  getSpecialty(vue, id) {
    const filters = {
      id: { field: 'id', value: id },
    };
    const expands = [];
    return this.getResources(vue, 'specialty', true, expands, filters);
  },

  updateSpecialty: async function (specialty) {
    const { data } = await requestHandler.put(
      `/?r=specialty/update&id=${specialty.id}`,
      specialty
    );
    return data;
  },

  createSpecialty: async function (specialty) {
    const { data } = await requestHandler.post(
      '/?r=specialty/create',
      specialty
    );
    return data;
  },

  deleteSpecialty(id) {
    return requestHandler.delete(`/?r=specialty/delete&id=${id}`);
  },

  // ------- Subspecialties ---------

  getSubspecialties(vue, filters, listParams) {
    return this.getResources(
      vue,
      'subspecialty',
      false,
      ['specialty', 'usersCount'],
      { ...filters, is_master: { field: 'is_master', value: 0 } },
      listParams
    );
  },

  getMasterSubspecialties(vue, filters, listParams) {
    return this.getResources(
      vue,
      'subspecialty',
      false,
      ['specialty'],
      { ...filters, is_master: { field: 'is_master', value: 1 } },
      listParams
    );
  },

  getAllSubspecialties() {
    return this.getResources(null, 'subspecialty', false, ['usersCount'], [], {
      perPage: 10000,
      sortBy: 'title',
    });
  },

  getSubspecialty(vue, id) {
    const filters = {
      id: { field: 'id', value: id },
    };
    const expands = [];
    return this.getResources(vue, 'subspecialty', true, expands, filters);
  },

  updateSubspecialty: async function (subspecialty) {
    const { data } = await requestHandler.put(
      '/?r=subspecialty/update',
      subspecialty,
      {
        params: {
          id: subspecialty.id,
        },
      }
    );
    return data;
  },

  createSubspecialty: async function (subspecialty) {
    const { data } = await requestHandler.post(
      '/?r=subspecialty/create',
      subspecialty
    );
    return data;
  },

  deleteSubspecialty(id) {
    return requestHandler.delete(`/?r=subspecialty/delete&id=${id}`);
  },

  // ------- questionnaires ---------

  getQuestionnaires(vue, filters, listParams) {
    return this.getResources(
      vue,
      'questionnaire',
      false,
      ['society', 'nonSocietyMembershipTypes'],
      filters,
      listParams
    );
  },

  getQuestionnaireWithQuestionsAndVariables(vue, id) {
    const filters = {
      id: { field: 'id', value: id },
    };
    const expands = [
      'society',
      'sections.specialtyIds',
      'sections.questionSections.question.variables.variableOptions',
      'sections.questionSections.question.specialties',
      'societyMembershipTypes',
      'nonSocietyMembershipTypes',
      'specialties',
    ];
    return this.getResources(vue, 'questionnaire', true, expands, filters);
  },

  cloneQuestionnaire(id) {
    return requestHandler.put(`/?r=questionnaire/clone&id=${id}`, {});
  },

  createQuestionnaire(questionnaire) {
    return requestHandler.post('/?r=questionnaire/create', questionnaire);
  },

  updateQuestionnaire: async function (questionnaire) {
    const expands = [
      'society',
      'sections.specialtyIds',
      'sections.questionSections.question.variables.variableOptions',
      'sections.questionSections.question.specialties',
      'societyMembershipTypes',
      'nonSocietyMembershipTypes',
      'specialties',
    ];
    const { data } = await requestHandler.put(
      `/?r=questionnaire/update&id=${questionnaire.id}`,
      questionnaire,
      {
        params: {
          expand: expands.join(','),
        },
      }
    );

    return data;
  },

  deleteQuestionnaire(id) {
    return requestHandler.delete(`/?r=questionnaire/delete&id=${id}`);
  },

  getQuestionnaireById: async function (id) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'questionnaire/view',
        id,
      },
    });
    return response.data;
  },

  // ------- questions ---------

  getQuestion: async function (id) {
    const expands = ['variables.variableOptions', 'specialties'];

    const response = await requestHandler.get('/', {
      params: {
        r: 'question/view',
        id,
        expand: expands.join(','),
      },
    });
    return response.data;
  },

  getQuestions(vue, filters, listParams) {
    return this.getResources(
      vue,
      'question',
      false,
      ['variables'],
      filters,
      listParams
    );
  },

  getAllQuestions(vue) {
    return this.getResources(
      vue,
      'question',
      false,
      ['society', 'variables.variableOptions', 'sections'],
      [],
      { perPage: 100000 }
    );
  },

  updateQuestion: async function (question) {
    const { data } = await requestHandler.put(
      `/?r=question/update&id=${question.id}`,
      question,
      {
        params: {
          expand: ['variables.variableOptions', 'sections'].join(','),
        },
      }
    );
    return data;
  },

  deleteQuestion(id) {
    return requestHandler.delete(`/?r=question/delete&id=${id}`);
  },

  createQuestion(question) {
    return requestHandler.post('/?r=question/create', question, {
      params: {
        expand: ['variables'].join(','),
      },
    });
  },

  copyQuestion: async function (id) {
    const { data } = await requestHandler.post(
      '/',
      {},
      {
        params: {
          r: 'question/copy',
          id,
          expand: ['variables.variableOptions', 'sections'].join(','),
        },
      }
    );
    return data;
  },

  // ------- variables --------

  getVariable(vue, id) {
    const filters = {
      id: { field: 'id', value: id },
    };
    const expands = ['question'];
    return this.getResources(vue, 'variable', true, expands, filters);
  },

  getAllVariables(vue) {
    return this.getResources(vue, 'variable', false, ['question'], [], {
      perPage: 100000,
    });
  },

  getAllConditionVariables: async function () {
    const { data } = await requestHandler.get(
      '/?r=variable/get-all-condition-variables',
      {
        params: {
          expand: 'question',
        },
      }
    );
    return data ? data.items : [];
  },

  // ------- surveys ---------

  getSurveys(vue, filters, listParams) {
    const expands = ['user', 'responses', 'questionsCompleted'];
    return this.getResources(
      vue,
      'survey',
      false,
      expands,
      filters,
      listParams
    );
  },

  getSurvey: async function (id, sectionId = null) {
    const expands = [
      'responses',
      ...USER_EXTRA_FIELDS,
      ...USER_QUESTIONNAIRE_EXTRA_FIELDS,
    ];
    const { data } = await requestHandler.get(
      '/?r=survey/get-with-default-job-profile',
      {
        params: {
          expand: expands.join(','),
          id,
          sectionId,
        },
      }
    );
    return data;
  },

  createSurvey: async function (questionnaireId) {
    const { data } = await requestHandler.post('/?r=survey/create', {
      questionnaire_id: questionnaireId,
      created: this.formatDate(),
    });
    return data;
  },

  updateSurvey: function (component, survey, submit = false, sectionId = null) {
    const putFnWithAbort = createPutFnWithAbort(
      component,
      'updateSurveyRequest'
    );
    let url = `/?r=survey/update&id=${survey.id}`;
    if (submit) {
      url += '&submit=1';
    }
    if (sectionId) {
      url += `&sectionId=${sectionId}`;
    }
    return putFnWithAbort(url, survey);
  },

  addTotalTakenTimeToSurvey: async function (surveyId, time) {
    const { data } = await requestHandler.post(
      '/',
      {
        time,
      },
      {
        params: {
          r: 'survey/add-seconds-to-total-time',
          id: surveyId,
        },
      }
    );
    return data;
  },

  submitSurvey: async function (vue, survey) {
    const { data } = await this.updateSurvey(vue, {
      ...survey,
      submitted: this.formatDate(),
    });
    await this.updateUser(vue, store.state.user, true);
    return data;
  },

  unsubmitSurvey(vue, survey) {
    survey.submitted = null;
    return this.updateSurvey(vue, survey).then(() => {
      this.updateUser(vue, store.state.user);
    });
  },

  deleteSurvey(id) {
    return requestHandler.delete(`/?r=survey/delete&id=${id}`);
  },
  resetSurveyByUserId: async function (userId) {
    return await requestHandler.post(
      '/?r=survey/reset-by-user-id',
      {},
      {
        params: {
          userId,
        },
      }
    );
  },
  resetSurvey: async function (surveyId) {
    return await requestHandler.post(
      '/?r=survey/reset',
      {},
      {
        params: {
          surveyId,
        },
      }
    );
  },

  getSurveyDetailsByQuestionnaireidAndUserid: async function (userId, questionnaireid) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'survey/get-survey-details-by-questionnaireid-userid',
        userId,
        questionnaireid,
      },
    });
    return data;
  },

  // ------- responses ---------

  updateResponses(responses) {
    responses.forEach((r) => {
      this.setResponseTimestamps(r);
    });
    // NB: per Yii routing case conventions, 'multi-update' => ResponseController::actionMultiUpdate
    return requestHandler.put('/?r=response/multi-update', {
      responses: responses,
    });
  },

  setResponseTimestamps(response) {
    if (response.created) {
      response.created = this.formatDate();
    } else {
      response.updated = this.formatDate();
    }
  },

  stripSeparators(val) {
    return val ? val.toString().replace(/[,]/g, '') : null;
  },

  // ------- survey results ---------

  getSurveyResults: async function (
    component,
    optionName,
    filters,
    qsId = null,
    scopeType = REPORTS_SCOPE_TYPES.STUDY,
    selectedSection = null
  ) {
    let requestParams = {
      r: 'results/search-results',
      scopeType,
      selectedSection
    };
    if (qsId) {
      requestParams.qsId = qsId;
    }
    const postWithAbort = createPostFnWithAbort(component, optionName);
    const { data } = await postWithAbort(
      '/',
      {
        filters,
      },
      {
        params: requestParams,
      }
    );
    return data;
  },

  getNonSocietySurveyResults: async function (
    component,
    optionName,
    filters,
    qsId = null,
    scopeType = REPORTS_SCOPE_TYPES.STUDY
  ) {
    let requestParams = {
      r: 'results/search-non-society-results',
      scopeType,
    };
    if (qsId) {
      requestParams.qsId = qsId;
    }
    const postWithAbort = createPostFnWithAbort(component, optionName);
    const { data } = await postWithAbort(
      '/',
      {
        filters,
      },
      {
        params: requestParams,
      }
    );
    return data;
  },

  getPresentationResults: async function (component, optionName, filters) {
    const postWithAbort = createPostFnWithAbort(component, optionName);
    const { data } = await postWithAbort(
      '/',
      {
        filters,
      },
      {
        params: {
          r: 'presentation/reports-presentation',
        },
      }
    );
    return data;
  },

  getSurveyResultsForDashboard: async function (sameSpecialty = false) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'results/search-for-dashboard',
        sameSpecialty: sameSpecialty ? 1 : 0,
      },
    });
    return data;
  },

  getDataFotReport: async function (id) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'results/get-data-for-report',
        id,
      },
    });
    return data;
  },

  /**
   * Job Question
   */
  //<editor-fold desc="Job Questions" defaultstate="collapsed">
  getAllJobQuestions: async function () {
    const response = await requestHandler.get('/?r=job-question/index', {
      params: {
        expand: ['jobQuestionVariables.variableOptions'].join(','),
      },
    });
    return (response.data || {}).items;
  },
  getAllActiveJobQuestions: async function () {
    const { data = [] } = await requestHandler.get(
      '/?r=job-question/get-active',
      {
        params: {
          expand: ['jobQuestionVariables.variableOptions'].join(','),
        },
      }
    );
    return data;
  },
  getJobQuestionsForQuestion: async function () {
    const { data } = await requestHandler.get(
      '/?r=job-question/get-for-question'
    );
    return data;
  },

  updateJobQuestion: async function (jobQuestion) {
    const { data } = await requestHandler.put(
      '/?r=job-question/update',
      jobQuestion,
      {
        params: {
          id: jobQuestion.id,
          expand: ['jobQuestionVariables.variableOptions'].join(','),
        },
      }
    );
    return data;
  },

  deleteJobQuestion: async function (id) {
    return requestHandler.delete('/?r=job-question/delete', {
      params: {
        id,
      },
    });
  },

  getJobQuestionsDataForProfile: async function (jobProfileId) {
    const expands = [
      'jobQuestionVariables.jobProfileQuestionResponses',
      'jobQuestionVariables.variableOptions',
      'questions.responses',
    ];
    const { data } = await requestHandler.get(
      '/?r=job-question/get-for-job-profile',
      {
        params: {
          jobProfileId,
          expand: expands.join(','),
        },
      }
    );
    return data;
  },
  //</editor-fold>

  //<editor-fold desc="Job Profile Question Response" defaultstate="collapsed">
  updateJobProfileQuestionResponses: async function (jobQuestionId, responses) {
    const { data } = await requestHandler.post('/', responses, {
      params: {
        r: 'job-profile-question-response/bulk-update',
        job_question_id: jobQuestionId,
      },
    });
    return data;
  },
  //</editor-fold>

  //<editor-fold desc="Job Question Prior Questinnaire Response" defaultstate="collapsed">
  savePriorQuestionnaireDefaultResponse: async function (surveyId,notChangeQuestions) {
   
    const { data } = await requestHandler.post('/?r=survey/save-prior-questionnaire-default-response', {
      surveyId:surveyId,
      notChangeQuestions: notChangeQuestions,
    });
    return data;

  },
  //</editor-fold>

  //<editor-fold desc="User Job Profiles actions" defaultstate="collapsed">
  getUserJobProfiles: async function () {
    const expands = [
      'jobQuestions.jobQuestionVariables.jobProfileQuestionResponses',
      'jobQuestions.jobQuestionVariables.variableOptions',
      'jobProfileJobOfferMatchesCount',
      'jobProfileJobOfferCoreMatchesCount',
      'jobProfileJobOfferPerfectMatchesCount',
      'jobProfileJobOfferMatches.jobOffer',
      'jobProfileJobOfferMatches.numberOfUnviewedMessages',
    ];
    const { data } = await requestHandler.get('/?r=job-profile/get-for-user', {
      params: {
        expand: expands.join(','),
      },
    });
    return (data || {}).items;
  },

  getDeletedJobProfiles: async function () {
    const expands = [
      'jobQuestions.jobQuestionVariables.jobProfileQuestionResponses',
      'jobQuestions.jobQuestionVariables.variableOptions',
      'jobProfileJobOfferMatchesCount',
      'jobProfileJobOfferCoreMatchesCount',
      'jobProfileJobOfferPerfectMatchesCount',
      'jobProfileJobOfferMatches.jobOffer',
      'jobProfileJobOfferMatches.numberOfUnviewedMessages',
    ];
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-profile/get-deleted',
        expand: expands.join(','),
      },
    });
    return (data || {}).items;
  },

  getJobOfferDetailsForJobProfile: async function (id, jpId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-profile/get-matched-offer-data',
        id,
        jpId,
        expand: ['jobQuestionResponses', 'cbsa_name'].join(','),
      },
    });
    return data;
  },

  getJobProfileData: async function (id) {
    const expands = ['jobQuestionIds', 'requiredJobQuestionIds'];
    const { data } = await requestHandler.get(
      '/?r=job-profile/get-single-for-user',
      {
        params: {
          id,
          expand: expands.join(','),
        },
      }
    );
    return data;
  },

  createUserJobProfile: async function (newProfile) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/create-for-user',
      newProfile
    );
    return data;
  },

  updateUserJobProfile: async function (updatedProfile) {
    const { data } = await requestHandler.put(
      '/?r=job-profile/update-for-user',
      updatedProfile,
      {
        params: {
          id: updatedProfile.id,
        },
      }
    );
    return data;
  },

  jobProfilePostToExchange: async function (updatedProfile) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/post-to-exchange',
      updatedProfile,
      {
        params: {
          id: updatedProfile.id,
        },
      }
    );
    return data;
  },

  createDuplicateToDefaultJobProfile: async function (updatedProfile,priorYearQuestionnaireId,newTitle) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/create-duplicate-to-default-job-profile',
      updatedProfile,
      {
        params: {
          id: updatedProfile.id,
          priorYearQuestionnaireId:priorYearQuestionnaireId,
          newTitle: newTitle
        },
      }
    );
    return data;
  },
  
  jobProfileCompleteStudy: async function (updatedProfile) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/complete-default-study',
      updatedProfile,
      {
        params: {
          id: updatedProfile.id,
        },
      }
    );
    return data;
  },

  deleteUserJobProfile: async function (id) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/delete-for-user',
      {},
      {
        params: {
          id,
        },
      }
    );
    return data;
  },

  unDeleteJobProfile: async function (id) {
    const { data } = await requestHandler.post(
      '/',
      {},
      {
        params: {
          r: 'job-profile/un-delete',
          id,
        },
      }
    );
    return data;
  },

  copyProfile: async function (profileData) {
    const { data } = await requestHandler.post(
      '/?r=job-profile/copy',
      profileData
    );
    return data;
  },

  declineJobOffer: async function (id, checkedReasons, otherReason) {
    const { data } = await requestHandler.post(
      '/',
      {
        checkedReasons,
        otherReason,
      },
      {
        params: {
          r: 'job-profile/decline-job-offer',
          id,
        },
      }
    );
    return data;
  },

  undeclineJobOffer: async function (id) {
    const { data } = await requestHandler.post(
      '/',
      {},
      {
        params: {
          r: 'job-profile/undecline-job-offer',
          id,
        },
      }
    );
    return data;
  },
  updateJobProfileJobOfferMatchStatus: async function (matchId,status) {
    const { data } = await requestHandler.put('/', {},{
      params: {
        r: 'job-profile/update-job-profile-job-offer-match-status',
        matchId,
        status,
      },
    });
    return data;
  },
  updateJobProfileDeclineStatus: async function (id, isDeclined) {
    const { data } = await requestHandler.put(
      '/',
      {},
      {
        params: {
          r: 'job-profile/update-decline-status',
          id,
          isDeclined,
        },
      }
    );
    return data;
  },
  //</editor-fold>

  //<editor-fold desc="Region actions" defaultstate="collapsed">

  getRegions: async function () {
    const { data } = await requestHandler.get('/?r=region/index');
    return data.items ? data.items : [];
  },

  getSubRegions: async function () {
    const { data } = await requestHandler.get('/?r=subregion/index');
    return data.items ? data.items : [];
  },

  getStates: async function () {
    const { data } = await requestHandler.get('/?r=state/index');
    return data.items ? data.items : [];
  },

  getCities: async function (stateId) {
    const { data } = await requestHandler.get(
      '/?r=zip-code-extension/get-unique-cities',
      {
        params: {
          stateId,
        },
      }
    );
    return data;
  },

  getCbsaValues: async function () {
    const { data } = await requestHandler.get('/?r=cbsa/get-unique');
    return data;
  },
  getCbsaClusterValues: async function () {
    const { data } = await requestHandler.get('/?r=cbsa/get-unique-from-cluster');
    return data;
  },
  //</editor-fold>
  // ------- misc ---------

  formatDate() {
    const d = dayjs().toISOString(); // 2013-02-04T22:44:30.652Z
    return d.split('.')[0].replace('T', ' ');
  },

  /**
   * A multi-purpose method for retrieving resources from the Yii api - this can be used to fetch
   * a single resource or a list; the filters and listParams can be bound to a table for
   * paging/sorting/filtering support.
   * @param vue Vue component instance
   * @param table Name of the database table
   * @param single Whether to fetch a single resource or a list
   * @param expands Names of has-many model relations to fetch with the main resource
   * @param filters Filters on model fields (use an 'id' filter to fetch a single resource)
   * @param listParams Paging/sorting options, bound to a table
   */
  getResources(
    vue,
    table,
    single,
    expands = [],
    filters = undefined,
    listParams = undefined
  ) {
    if (vue) {
      vue.$wait.start('loading');
    }
    let params = { r: table };
    if (expands && expands.length) {
      params.expand = expands.join(',');
    }
    if (listParams) {
      params.perPage = listParams.perPage;
      params.page = listParams.currentPage - 1;
      params.sort = (listParams.sortDesc ? '-' : '') + listParams.sortBy;
    }
    if (filters) {
      params.filters = filters;
    }    
    return requestHandler
      .get('/', {
        params: params,
      })
      .then(
        function success(response) {
          if (vue) {
            vue.$wait.end('loading');
          }
          if (listParams) {
            listParams.totalRows = response.data._meta.totalCount;
          }
          if (single) {
            return response.data.items[0];
          } else {
            return response.data.items;
          }
        },
        function error(response) {
          if (vue) {
            vue.$wait.end('loading');
          }
          console.log('error', response);
        }
      );
  },

  /**
   * SETTINGS
   */

  getSettings() {
    return requestHandler.get('/?r=settings/frontend').then(
      (response) => {
        return response.data;
      },
      //error
      (response) => {
        console.log('error', response);
      }
    );
  },

  getSettingsList(vue, filters, listParams) {
    return this.getResources(vue, 'settings', false, [], filters, listParams);
  },

  getSetting(vue, key) {
    return requestHandler.get(`/?r=settings/view&id=${key}`).then(
      (response) => {
        return response.data;
      },
      //error
      (response) => {
        console.log('error', response);

        vue.$toasted.error(`${response.data.message}: Key: ${key}`, {
          duration: 3000,
        });

        vue.$router.push({ path: '/admin/settings' });
      }
    );
  },

  updateSetting: async function (key, value) {
    return await requestHandler.put(`/?r=settings/update&key=${key}`, {
      value,
    });
  },

  getResetPasswordTimeoutSetting: async function () {
    const { data } = await requestHandler.get(
      '/?r=settings/get-reset-password-timeout'
    );
    return data;
  },

  /**
   * DynamicContent
   */
  //<editor-fold desc="Dynamic Content" defaultstate="collapsed">
  getFaqDynamicContent: async function () {
    const { data } = await requestHandler.get('/?r=dynamic-content/faq');
    return data;
  },

  getIcoNoStudyCompletedDynamicContent: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/ico-no-study-completed',
      },
    });
    return data;
  },

  getReferAColleagueDynamicContent: async function (isRecruiter) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/refer-a-colleague',
        isRecruiter,
      },
    });
    return data;
  },

  getJobBuilderLanding: async function () {
    const { data } = await requestHandler.get('/?r=dynamic-content/job-builder-landing');
    return data;
  },
  
  getDynamicContentByRoute: async function (route) {
    const { data } = await requestHandler.get(`/?r=dynamic-content/${route}`);
    return data;
  },

  getWelcomeToPhairifyContent: async function () {
    const { data } = await requestHandler.get(
      '/?r=dynamic-content/welcome-to-phairify'
    );
    return data;
  },

  getQuickRegistrationWelcomeContent: async function (memberId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/quick-registration-welcome',
        memberId,
      },
    });
    return data;
  },

  getLongRegistrationWelcomeContent: async function (memberId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/long-registration-welcome',
        memberId,
      },
    });
    return data;
  },

  getICOStudyFAQContent: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/ico-study-faq',
      },
    });
    return data;
  },

  getRegistrationHelpText: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/registration-help-text',
      },
    });
    return data;
  },

  getIcoPostInfo1Content: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/ico-post-info1',
      },
    });
    return data;
  },

  getBeginStudyMessage: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/begin-study-message',
      },
    });
    return data;
  },

  getPriorStudyMessage: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/prior-study-message',
      },
    });
    return data;
  },

  getInterestedQuestionContent: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/interested-question-content',
      },
    });
    return data;
  },
  
  getThankYouCompletingStudyMessage: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'dynamic-content/thank-you-completing-study-message',
      },
    });
    return data;
  },

  //</editor-fold>

  /**
   * SurveyHome
   */

  getQuestionnairesForSurveyHome: async function () {
    const response = await requestHandler.get(
      '/?r=questionnaire/get-for-survey-home'
    );
    return response.data;
  },

  getQuestionnaireWithoutUserSurvey: async function () {
    const response = await requestHandler.get(
      '/?r=questionnaire/get-without-user-survey'
    );
    return response.data;
  },

  sendInvitationToMembers: async function (
    societyId,
    allowResending,
    doNotOverwriteTokens
  ) {
    const response = await requestHandler.post(
      '/?r=society-member-email/send-invitation-to-members',
      {
        society_id: societyId,
        allow_resending: allowResending,
        do_not_overwrite: doNotOverwriteTokens,
      }
    );
    return response.data;
  },

  sendIndividualInvitation: async function (memberId) {
    const response = await requestHandler.post(
      '/?r=society-member-email/send-invitation-individually',
      { member_id: memberId },
      {
        params: {
          expand: SOCIETY_MEMBER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return response.data;
  },

  getSocietyMemberDataByToken: async function (token) {
    const response = await requestHandler.post(
      '/?r=auth/get-society-member-data-by-token',
      { token },
      {
        params: {
          expand: [
            'society',
            'zip_code',
            'gender',
            'ethnicity',
            'year_of_birth',
            'societyMemberTypeMapping',
          ].join(','),
        },
      }
    );
    return response.data;
  },

  getAllSocietyMembers: async function (societyId) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'society-member-email/get-society-members',
        society_id: societyId,
        expand: [
          ...SOCIETY_MEMBER_EXTRA_FIELDS,
          'user.primaryActiveSurvey',
        ].join(','),
      },
    });
    return (response.data || {}).items;
  },

  getRegistrationToken: async function (id) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'society-member-email/get-registration-token',
        id,
      },
    });
    return response.data;
  },

  enableSocietyMember: async function (memberId) {
    const response = await requestHandler.post(
      '/?r=society-member-email/activate-disabled',
      { member_id: memberId },
      {
        params: {
          expand: SOCIETY_MEMBER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return response.data;
  },

  registerUnregisteredUsers: async function (societyId) {
    const { data } = await requestHandler.post(
      '/?r=society-member-email/create-dummy-users',
      { societyId },
      {
        params: {
          expand: SOCIETY_MEMBER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return data;
  },

  getParticipationInfo: async function () {
    const response = await requestHandler.get(
      '/?r=survey/get-participation-info'
    );
    return response.data;
  },

  getQuestionnaireComparisonSettings: async function (qId = null) {
    const params = {
      r: 'user/get-questionnaire-comparisons',
    };
    if (qId) {
      params.qId = qId;
    }
    const { data } = await requestHandler.get('/', {
      params,
    });
    const { comparisons, questionnaire } = data || {};
    if (!comparisons) return { comparisons: null, questionnaire };
    return { comparisons: JSON.parse(comparisons), questionnaire };
  },

  getPresentationComparisonSettings: async function () {
    const { data } = await requestHandler.get(`/`, {
      params: {
        r: 'presentation/presentational-comparison',
      },
    });
    return data;
  },

  updateQuestionnaireComparisons: async function (qId, comparisons) {
    const response = await requestHandler.post(
      `/?r=user/update-questionnaire-comparisons&qId=${qId}`,
      {
        comparisons,
      }
    );
    return response.data;
  },

  /**
   * MSS ADMIN
   */
  mssAdminGetSociety: async function () {
    const response = await requestHandler.get(
      '/?r=mssAdmin/society/get-for-dashboard'
    );
    return response.data;
  },

  mssAdminGetSpecialtiesStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/society/specialties-statistic',
      },
    });
    return data;
  },

  mssAdminGetProfileDataStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/society/profile-data-statistic',
      },
    });
    return data;
  },

  mssAdminGetLocationStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/society/location-statistic',
      },
    });
    return data;
  },

  mssAdminGetQuestionResultsForDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/society/question-results',
      },
    });
    return data;
  },

  mssAdminGetStatisticsOfMembers: async function () {
    const response = await requestHandler.get(
      '/?r=mssAdmin/dashboard/get-statistics-of-members'
    );
    return response.data;
  },
  mssAdminGetIcoStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/job-profile/ico-statistic',
      },
    });
    return data;
  },

  mssAdminGetIcoLocationStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/job-profile/ico-location-statistic',
      },
    });
    return data;
  },

  mssAdminGetIcoValuesForDashboardCharts: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mssAdmin/job-profile/ico-values-for-dashboard-charts',
      },
    });
    return data;
  },

  mssAdminUpdateSociety: function (society) {
    return requestHandler.put('/?r=mssAdmin/society/update', society);
  },

  adminGetActiveSurveys: async function (sId, isGmAdmin = false) {
    const response = await requestHandler.get(
      `/?r=mssAdmin/survey/get-active-surveys`,
      {
        params: {
          societyId: sId,
          isGmAdmin,
          expand: ['societyMembershipTypes'].join(','),
        },
      }
    );
    return response.data;
  },
  adminGetUpcomingSurveys: async function (sId, isGmAdmin = false) {
    const response = await requestHandler.get(
      `/?r=mssAdmin/survey/get-upcoming-surveys`,
      {
        params: {
          societyId: sId,
          isGmAdmin,
          expand: ['societyMembershipTypes'].join(','),
        },
      }
    );
    return response.data;
  },
  adminGetCompletedSurveys: async function (sId, isGmAdmin = false) {
    const response = await requestHandler.get(
      `/?r=mssAdmin/survey/get-completed-surveys`,
      {
        params: {
          societyId: sId,
          isGmAdmin,
          expand: ['societyMembershipTypes'].join(','),
        },
      }
    );
    return response.data;
  },
  adminGetAllSurveys: async function (sId) {
    const response = await requestHandler.get(`/`, {
      params: {
        r: 'survey/get-all-studies-for-society',
        societyId: sId,
      },
    });
    return response.data;
  },
  getSurveyResultsForAdmin: async function (
    questionnaireId,
    filters,
    scopeType = REPORTS_SCOPE_TYPES.STUDY
  ) {
    const result = await requestHandler.post(
      '/',
      { filters },
      {
        params: {
          r: 'mssAdmin/survey/get-results',
          questionnaireId,
          scopeType,
        },
      }
    );
    return result.data;
  },
  getSocietyMembershipTypes: async function () {
    const response = await requestHandler.get(
      '/?r=society-membership-type/index'
    );
    return response.data;
  },
  getSocietyMemberTypeMappings: async function () {
    const response = await requestHandler.get('/', {
      params: {
        r: 'society-member-type-mapping/index',
        expand: ['societyMembershipType'].join(','),
      },
    });
    return response.data;
  },
  bulkUpdateSocietyMemberTypeMappings: async function (mappings) {
    const response = await requestHandler.put(
      `/?r=society-member-type-mapping/bulk-update`,
      mappings
    );

    return response.data;
  },
  deleteSocietyMemberTypeMapping: async function (id) {
    return await requestHandler.delete(
      `/?r=society-member-type-mapping/delete&id=${id}`
    );
  },
  getStudyTypes: async function () {
    const response = await requestHandler.get('/?r=study-type/index');
    return response.data;
  },

  getPrimaryQuestionnaireQuestionsCount: async function (
    societyId,
    societyMembershipId
  ) {
    const response = await requestHandler.get(
      '/?r=society/primary-questionnaire-questions-count',
      {
        params: {
          society_id: societyId,
          society_membership_id: societyMembershipId,
        },
      }
    );
    return response.data;
  },

  /**
   * GM ADMIN
   */
  /**
   * Internal Persons
   */
  getInternalPersons: async function () {
    const response = await requestHandler.get(
      '/?r=gmAdmin/internal-person/index'
    );
    return response.data;
  },
  disableInternalPerson: async function (id) {
    const response = await requestHandler.post(
      '/?r=gmAdmin/internal-person/disable',
      { id: id }
    );
    return response.data;
  },
  createInternalPerson: async function (person) {
    return requestHandler.post('/?r=gmAdmin/internal-person/create', person, {
      params: {
        expand: 'societies',
      },
    });
  },
  updateInternalPerson(person) {
    return requestHandler.put(
      `/?r=gmAdmin/internal-person/update&id=${person.id}`,
      person,
      {
        params: {
          expand: 'societies',
        },
      }
    );
  },
  deleteInternalPerson(id) {
    return requestHandler.delete(`/?r=gmAdmin/internal-person/delete&id=${id}`);
  },
  getActiveInternalPersons: async function () {
    const response = await requestHandler.get(
      '/?r=gmAdmin/internal-person/get-active'
    );
    return response.data;
  },
  gmAdminGetOverviewData: async function () {
    const response = await requestHandler.get(
      '/?r=gmAdmin/dashboard/get-overview-data'
    );
    return response.data;
  },

  gmAdminResetStudies: async function (questionnaireId) {
    return await requestHandler.post(
      '/?r=questionnaire/reset',
      {},
      {
        params: {
          questionnaireId,
        },
      }
    );
  },

  /**
   * Board Certifications
   */
  //<editor-fold desc="Board Certifications" defaultstate="collapsed">
  getCertifyingBoards: async function () {
    const response = await requestHandler.get('/?r=certifying-board/index');
    return response.data;
  },
  getBoardCertifications: async function () {
    const response = await requestHandler.get('/?r=board-certification/index');
    return response.data;
  },
  getSocietyCertifyingBoards: async function (societyId = null) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'certifying-board/society-certifying-board',
        societyId,
      },
    });
    return response.data;
  },
  //</editor-fold>

  /**
   * Recruiter
   */
  //<editor-fold desc="Recruiter" defaultstate="collapsed">
  getJobQuestionsForIcoBrowser: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/ico-browser/get-job-questions',
        expand: ['jobQuestionVariables.variableOptions'].join(','),
      },
    });
    return data;
  },
  getJobQuestionsForGuestIcoBrowser: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'guest-ico-browser/get-job-questions',
        expand: ['jobQuestionVariables.variableOptions'].join(','),
      },
    });
    return data;
  },
  getSettingByKey: async function (key) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'guest-settings/get-setting-by-key',
        key
      },
    });
    return data;
  },
  getJobQuestionsForJobOffer: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-question/get-active',
        expand: ['jobQuestionVariables.variableOptions'].join(','),
      },
    });
    return data;
  },
  getJobQuestionResultsForIcoBrowser: async function (jobQuestionId, filters) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/ico-browser/get-job-question-results',
        jobQuestionId,
        filters,
      },
    });
    return data;
  },
  getCityByZipCode: async function (zipCode) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/ico-browser/get-city-by-zip-code',
        zipCode,
      },
    });
    return data;
  },
  getRecruiterProfile: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/recruiter-profile',
      },
    });
    return data;
  },
  updateRecruiterProfile: async function (profileData) {
    const { data } = await requestHandler.put('/', profileData, {
      params: {
        r: 'recruiter/update-recruiter-profile',
      },
    });
    return data;
  },
  getRecruiterProfileForAdmin: async function (userId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/recruiter-profile-for-admin',
        id: userId,
      },
    });
    return data;
  },
  updateRecruiterByAdmin: async function (profileData) {
    const { data } = await requestHandler.put(`/`, profileData, {
      params: {
        r: 'recruiter/update-recruiter-by-admin',
        id: profileData.id,
        expand: USER_EXTRA_FIELDS.join(','),
      },
    });
    return data;
  },
  async createRecruiter(newRecruiter) {
    if (!store.getters.isAuthenticated || !store.getters.userIsGMAdmin) return;
    const { data } = await requestHandler.post('/', newRecruiter, {
      params: {
        r: 'recruiter/admin-recruiter-create',
      },
    });
    return data;
  },

  verifySignUpToken: async function (token) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'auth/recruiter-verify-sign-up-token',
        token,
      },
    });
    return data;
  },

  recruiterRepeatVerificationEmail: async function (token = null) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'auth/recruiter-repeat-verification-email',
        token,
      },
    });
    return data;
  },

  getPaidSpecialties: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/get-paid-specialties',
        expand: [
          'questionnaires.sections.questionSections.question.variables.variableOptions',
        ].join(','),
      },
    });
    return data;
  },
  getRecruiterSurveyResults: async function (
    component,
    optionName,
    filters,
    qsId = null,
    scopeType = REPORTS_SCOPE_TYPES.STUDY
  ) {
    let requestParams = {
      r: 'recruiter/search-results',
      scopeType,
    };
    if (qsId) {
      requestParams.qsId = qsId;
    }
    const postWithAbort = createPostFnWithAbort(component, optionName);
    const { data } = await postWithAbort(
      '/',
      {
        filters,
      },
      {
        params: requestParams,
      }
    );
    return data;
  },
  getRecruiterLatestSurveyResults: async function (
    ids
  ) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/find-latest-study-by-questionnaire-ids',
        ids,
      },
    });

    return data;
  },
  getJobInformationSets: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/job-information-set/index',
      },
    });
    return data.items;
  },
  createJobInformationSet: async function (jobInformationSet) {
    const { data } = await requestHandler.post('/', jobInformationSet, {
      params: {
        r: 'recruiter/job-information-set/create',
      },
    });
    return data;
  },
  updateJobInformationSet: async function (jobInformationSet) {
    const { data } = await requestHandler.put(``, jobInformationSet, {
      params: {
        r: `recruiter/job-information-set/update`,
        id: jobInformationSet.id,
      },
    });
    return data;
  },
  deleteJobInformationSet: async function (id) {
    return requestHandler.delete(`/`, {
      params: {
        r: `recruiter/job-information-set/delete`,
        id,
      },
    });
  },
  //</editor-fold>

  /**
   * 
   * Coupon Codes 
   */
  getCouponDiscountIfExist: async function (couponCode,offerPrice) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/job-offer/coupon-discount',
        couponCode,
        offerPrice
      },
    });
    return data;
  },
  //</editor-fold>

  /**
   * Job Offers
   */
  //<editor-fold desc="Job Offers" defaultstate="collapsed">
  getUserJobOffers: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/job-offer/user-offers',
        expand: [
          'specialty',
          'subspecialty',
          'user',
          'jobProfileJobOfferMatchesCount',
          'jobOfferJobQuestionResponses',
        ].join(','),
      },
    });
    return data.items || [];
  },

  getJobOfferData: async function (joId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: `recruiter/job-offer/view`,
        id: joId,
        expand: ['jobQuestionResponses'].join(','),
      },
    });
    return data;
  },

  getMatchingJobProfilesByJobOfferId: async function (joId, params) {
    params.r = `recruiter/job-offer/get-job-profiles`;
    params.id = joId;
    const { data } = await requestHandler.get('/', {
      params: params,
    });
    return data;
  },

  purchaseJobOffer: async function (token, meta) {
    const { data } = await requestHandler.post(
      '/',
      {
        token,
        meta,
      },
      {
        params: {
          r: 'recruiter/job-offer/purchase',
          expand: ['jobQuestionResponses'].join(','),
        },
      }
    );
    return data;
  },

  invoiceJobOffer: async function (meta) {
    const { data } = await requestHandler.post(
      '/',
      {
        meta,
      },
      {
        params: {
          r: 'recruiter/job-offer/invoice',
          expand: ['jobQuestionResponses'].join(','),
        },
      }
    );
    return data;
  },

  createJobOfferData: async function (offerData, postItNow = false) {
    const { data } = await requestHandler.post('/', offerData, {
      params: {
        r: `recruiter/job-offer/create`,
        postItNow,
      },
    });
    return data;
  },

  updateEditFlag: async function (joId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: `recruiter/job-offer/update-edit-flag`,
        id: joId
      },
    });
    return data;
  },

  updateJobOfferData: async function (offerData, postItNow = false) {
    const { data } = await requestHandler.post('/', offerData, {
      params: {
        r: `recruiter/job-offer/update`,
        id: offerData.id,
        postItNow,
        expand: ['jobOfferJobQuestionResponses'].join(','),
      },
    });
    return data;
  },
  getNumberOfJobOfferMatches: async function (id) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'recruiter/job-offer/get-number-of-matches',
        id,
      },
    });
    return data;
  },

  allJobOffersForAdmin: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: `gmAdmin/job-offer/index`,
        expand: 'userEmail',
      },
    });
    return data || [];
  },

  updateJobOfferDataByGmAdmin: async function (offerData) {
    const { data } = await requestHandler.put('/', offerData, {
      params: {
        r: `gmAdmin/job-offer/update`,
        id: offerData.id,
        expand: ['userEmail', 'jobOfferJobQuestionResponses'].join(','),
      },
    });
    return data;
  },

  unpostJobOffer: async function (id) {
    const { data } = await requestHandler.put(
      '/',
      {},
      {
        params: {
          r: `gmAdmin/job-offer/unpost`,
          id,
          expand: 'userEmail',
        },
      }
    );
    return data;
  },

  setJobOfferStatusFilled: async function (id) {
    const { data } = await requestHandler.put(
      '/',
      {},
      {
        params: {
          r: `recruiter/job-offer/set-filled-status`,
          id,
        },
      }
    );
    return data;
  },

  setJobOfferStatusClosed: async function (id) {
    const { data } = await requestHandler.put(
      '/',
      {},
      {
        params: {
          r: `recruiter/job-offer/set-closed-status`,
          id,
        },
      }
    );
    return data;
  },

  deleteJobOffer: async function (id) {
    const { data } = await requestHandler.delete('/', {
      params: {
        r: 'gmAdmin/job-offer/delete',
        id,
      },
    });
    return data;
  },
  //</editor-fold>

  refreshTokenRequest: async function () {
    const { data } = await requestHandler.post('/?r=auth/refresh-token');
    return data;
  },

  deleteRefreshToken: async function () {
    const { data } = await requestHandler.delete('/?r=auth/refresh-token');
    return data;
  },

  getSocietyKpiReportData: async function (societyId, cutoffDate) {
    const response = await requestHandler.get('/?r=society/generate-kpi', {
      params: {
        societyId,
        cutoffDate,
      },
    });
    return response.data;
  },
  updateSocietyCertifyingBoards: async function (societyId, boardIds) {
    const response = await requestHandler.post(
      `/`,
      { boardIds },
      {
        params: {
          r: 'society/update-certifying-boards',
          id: societyId,
        },
      }
    );

    return response.data;
  },

  createDemoMember: async function (societyId, email) {
    const { data } = await requestHandler.post(
      '/',
      {
        society_id: societyId,
        email,
      },
      {
        params: {
          r: 'society-member-email/create-demo-member',
          expand: SOCIETY_MEMBER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return data;
  },

  getQuestionnairesByQuestionId: async function (questionId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'questionnaire/find-for-question',
        questionId,
        expand: ['studyType', 'societyMembershipTypes', 'society'].join(','),
      },
    });
    return data && data.items ? data.items : [];
  },

  getJobOfferDeclineReasons: async function () {
    const response = await requestHandler.get(
      '/?r=job-offer-decline-reason/get-all-reasons'
    );
    return response.data;
  },

  /**
   * Recruiter matching
   */
  //<editor-fold desc="Recruiter matching" defaultstate="collapsed">

  findNumberOfMatches: async function (criteria,postItNow=0) {
    const { data } = await requestHandler.post('/', criteria, {
      params: {
        r: 'recruiter/matching/find',
        postItNow:postItNow,
      },
    });
    return data;
  },
  findNumberOfMatchesOnGuestIcoBrowser: async function (criteria) {
    const { data } = await requestHandler.post('/', criteria, {
      params: {
        r: 'guest-ico-browser/find-number-of-matches',
      },
    });
    return data;
  },

  //</editor-fold>

  //<editor-fold desc="Study Statistics" defaultstate="collapsed">

  getSpecialtiesStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'society/specialties-statistic',
      },
    });
    return data;
  },

  getProfileDataStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'society/profile-data-statistic',
      },
    });
    return data;
  },

  getLocationStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'society/location-statistic',
      },
    });
    return data;
  },

  //</editor-fold>

  //<editor-fold desc="Mock Data Statistics" defaultstate="collapsed">

  getMockSpecialtiesStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/specialties-statistic',
      },
    });
    return data;
  },

  getMockProfileDataStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/profile-data-statistic',
      },
    });
    return data;
  },

  getMockLocationStatistic: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/location-statistic',
      },
    });
    return data;
  },
  getMockIcoStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/ico-statistic',
      },
    });
    return data;
  },

  getMockIcoLocationStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/ico-location-statistic',
      },
    });
    return data;
  },

  getMockIcoValuesForDashboardCharts: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'mock-data/ico-values-for-dashboard-charts',
      },
    });
    return data;
  },

  //</editor-fold>

  //<editor-fold desc="ICO Statistics" defaultstate="collapsed">

  getIcoStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-profile/ico-statistic',
      },
    });
    return data;
  },

  getIcoLocationStatistics: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-profile/ico-location-statistic',
      },
    });
    return data;
  },

  getDefaultJobProfile: async function (userId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-profile/get-default-job-profile',
        userId,
      },
    });
    return data;
  },
  
  getIcoValuesForDashboardCharts: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'job-question/ico-values-for-dashboard-charts',
      },
    });
    return data;
  },

  //</editor-fold>

  //<editor-fold desc="Logger" defaultstate="collapsed">

  logUserEvent: function (event, parameters = {}) {
    const { data } = requestHandler.post(
      '/',
      {
        event,
        parameters,
      },
      {
        params: {
          r: 'logger/create-event',
        },
      }
    );
    return data;
  },

  //</editor-fold>

  //<editor-fold desc="Sections" defaultstate="collapsed">
  getStudySectionStatics: async function (studyId, sectionId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'survey/section-statistics',
        id: studyId,
        sectionId,
      },
    });
    return data;
  },
  getCompletedModules: async function (questionnaireId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'survey/completed-sections',
        questionnaireId,
      },
    });
    return data;
  },
  //</editor-fold>

  //<editor-fold desc="Landing Dashboard Page" defaultstate="collapsed">
  getSurveyResultsForLandingDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'landing-dashboard/results-for-landing-dashboard-charts',
      },
    });
    return data;
  },
  getQuestionnaireForLandingDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'landing-dashboard/questionnaire',
      },
    });
    return data;
  },

  getIcoStatisticsLandingDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'landing-dashboard/ico-statistic',
      },
    });
    return data;
  },

  getIcoLocationStatisticsLandingDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'landing-dashboard/ico-location-statistic',
      },
    });
    return data;
  },

  getIcoValuesForDashboardChartsLandingDashboard: async function () {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'landing-dashboard/ico-values-for-dashboard-charts',
      },
    });
    return data;
  },
  //</editor-fold>

  signInUserByTempToken: async function (token) {
    let { data } = await requestHandler.post(
      '/',
      {
        token,
      },
      {
        params: {
          r: 'auth/signin-by-temp-token',
          expand: USER_EXTRA_FIELDS.join(','),
        },
      }
    );
    return data;
  },

  //<editor-fold desc="Messages" defaultstate="collapsed">
  getMessagesForMatchedOffer: async function (matchId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'message/get-for-matched-offer',
        matchId,
      },
    });
    return data;
  },
  getJobOfferMessages: async function (jobOfferId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'message/get-for-job-offer',
        jobOfferId,
      },
    });
    return data;
  },

  createNewMessage: async function (matchId, msgData) {
    const { data } = await requestHandler.post('/', msgData, {
      params: {
        r: 'message/create-for-matched-offer',
        matchId,
      },
    });
    return data;
  },

  markMessageAsViewed: async function (messageId) {
    const { data } = await requestHandler.put(
      '/',
      {},
      {
        params: {
          r: 'message/mark-as-viewed',
          messageId,
        },
      }
    );
    return data;
  },

  getMatchedOfferUnviewedCount: async function (matchId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'message/matched-offer-unviwed-count',
        matchId,
      },
    });
    return data;
  },

  getJobOfferUnviewedCount: async function (jobOfferId) {
    const { data } = await requestHandler.get('/', {
      params: {
        r: 'message/job-offer-unviwed-count',
        jobOfferId,
      },
    });
    return data;
  },

  getQuestionnaireSurvayById: async function (id) {
    const response = await requestHandler.get('/', {
      params: {
        r: 'questionnaire/view-survey',
        id,
      },
    });
    return response.data;
  },

  //</editor-fold>
};
